import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { SaveFolderInput } from '@core/types';
import { Store } from '@ngrx/store';
import { saveUserFolder, setReportFolder, setScheduleFolder } from '@unified-ui/user-folders';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions, GridReadyEvent, ModuleRegistry, RowDataUpdatedEvent, RowDragEndEvent, RowDragMoveEvent, RowSelectionOptions, SelectionChangedEvent, ValidationModule } from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { timer } from 'rxjs';
import { gridOptions } from './folder-grid.aggrid.options';
import { IFileDropIndicator, IFolderDropEvent, IFolderGridFile } from './types/folder-grid.type';
import { getFileDropIndicator, moveFiles } from './utils/folder-grid.utils';

ModuleRegistry.registerModules([AllEnterpriseModule, ValidationModule]);

//More info about how the drag and drop works here https://www.ag-grid.com/angular-data-grid/tree-data-row-dragging/
@Component({
    selector: 'unified-ui-folder-grid',
    imports: [AgGridAngular, ButtonModule],
    providers: [ConfirmationService],
    templateUrl: './folder-grid.component.html',
    styleUrl: './folder-grid.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FolderGridComponent {
    private _store = inject(Store);
    public onReportsChanged = output();
    public rowData = input.required<IFolderGridFile[]>();
    public dropNotAllowedFn = input<(target: any) => boolean>(() => false);
    // A single column has been defined by default as an example of how to define a folder column.
    public columnDefs = input<ColDef[]>([
        {
            headerName: 'Name',
            field: 'name',
            showRowGroup: true,
            minWidth: 300,
            rowDrag: true,
            cellRenderer: 'agGroupCellRenderer',
            cellRendererParams: {
                suppressCount: true
            }
        }
    ]);
    public gridOptions = input<GridOptions>(gridOptions);
    public onSelectedDataChanged = output<IFolderGridFile[]>();
    public onFoldersDataChanged = output<IFolderGridFile[]>();
    public onFolderDropped = output<IFolderDropEvent>();
    public onRowClicked = output<IFolderGridFile>();
    public gridReady = output<GridReadyEvent>();
    public rowDataUpdated = output<RowDataUpdatedEvent>();
    public rowSelection = input<RowSelectionOptions | 'single' | 'multiple' | undefined>('multiple');
    private gridApi!: GridApi<IFolderGridFile>;
    rowSelectionBuilder = computed(() => {
        const rowSelectionBase: RowSelectionOptions = {
            mode: 'singleRow',
            checkboxes: true,
            enableClickSelection: false,
            enableSelectionWithoutKeys: true,
            hideDisabledCheckboxes: true
        };
        switch (this.rowSelection()) {
            case 'single': {
                return { ...rowSelectionBase, mode: 'singleRow' } as RowSelectionOptions;
            }
            case 'multiple': {
                return { ...rowSelectionBase, mode: 'multiRow', groupSelects: 'self' } as RowSelectionOptions;
            }
            default: {
                return this.rowSelection();
            }
        }
    });

    protected onGridReady(params: GridReadyEvent<IFolderGridFile>) {
        this.gridReady.emit(params);
        this.gridApi = params.api;
    }

    private _isDropNotAllowed(target: IFolderGridFile) {
        return this.dropNotAllowedFn()(target.content!);
    }

    //More info about this https://www.ag-grid.com/angular-data-grid/tree-data-row-dragging/
    protected onRowDragEnd(event: RowDragEndEvent<IFolderGridFile>) {
        const source = event.node.data;
        const target = event.overNode?.data;
        if (target && this._isDropNotAllowed(target) && target.parentId) {
            return;
        }
        if (!source || source.parentId === target?.id || source?.id === target?.id) {
            this.gridApi.setRowDropPositionIndicator(null);
            return;
        }
        const reorderOnly = event.event?.shiftKey;
        const rowData = this.gridApi.getGridOption('rowData') ?? [];
        const newRowData = moveFiles(rowData, source, target, !!reorderOnly);
        if (newRowData !== rowData && source.parentId !== target?.id) {
            if (source.type === 'folder') {
                this._moveFolders(source, target);
            } else {
                source.fileType === 'schedule' ?
                    this._store.dispatch(setScheduleFolder({ scheduleFolderInput: { scheduleId: source.id, folderId: target?.type === 'folder' ? target?.id : target?.parentId } }))
                :   this._store.dispatch(setReportFolder({ reportFolderInput: { reportId: source.id, folderID: target?.type === 'folder' ? target?.id : target?.parentId } }));
                // give time to send the request to BE before trigging the emit to get a new list
                timer(200).subscribe(() => this.onReportsChanged.emit());
            }
            this.gridApi.setGridOption('rowData', newRowData);
        }
        this.gridApi.setRowDropPositionIndicator(null);
        this._showExtractedRowData(event.api);
    }

    //More info about this https://www.ag-grid.com/angular-data-grid/tree-data-row-dragging/
    protected onRowDragCancel() {
        this.gridApi.setRowDropPositionIndicator(null);
    }

    //More info about this https://www.ag-grid.com/angular-data-grid/tree-data-row-dragging/
    protected onRowDragMove(event: RowDragMoveEvent<IFolderGridFile>) {
        const source = event.node.data;
        const target = event.overNode?.data;
        if (target && this._isDropNotAllowed(target) && target.parentId) {
            return;
        }
        const reorderOnly = event.event?.shiftKey;
        const rowData = this.gridApi.getGridOption('rowData') ?? [];
        const indicator: IFileDropIndicator | null = getFileDropIndicator(rowData, source, target, !!reorderOnly);
        if (indicator) {
            // Find the row node by file reference
            let rowNode = null;
            this.gridApi.forEachNode(node => {
                if (node.data === indicator.file) {
                    rowNode = node;
                }
            });
            if (rowNode) {
                this.gridApi.setRowDropPositionIndicator({
                    row: rowNode,
                    dropIndicatorPosition: indicator.dropIndicatorPosition
                });
            } else {
                this.gridApi.setRowDropPositionIndicator(null);
            }
        } else {
            this.gridApi.setRowDropPositionIndicator(null);
        }
    }

    protected onSelectionChanged(selection: SelectionChangedEvent) {
        this.onSelectedDataChanged.emit(selection.api.getSelectedRows());
    }

    //More info about this https://www.ag-grid.com/angular-data-grid/tree-data-row-dragging/
    private _extractRowData(api: GridApi<IFolderGridFile>) {
        const extractedData: IFolderGridFile[] = [];
        api.forEachLeafNode(node => {
            let data = node.data!;
            const parentId = node.parent?.data?.id;
            if (data.parentId !== parentId) {
                // We create a new object only if the parentId has changed
                data = { ...data, parentId };
            }
            extractedData.push(data);
        });
        return extractedData;
    }

    //More info about this https://www.ag-grid.com/angular-data-grid/tree-data-row-dragging/
    private _showExtractedRowData(api: GridApi<IFolderGridFile>) {
        const extractedRowData = this._extractRowData(api);
        this.onFoldersDataChanged.emit(extractedRowData);
    }

    /**
     * Move folders to another folders or to root
     * @param source source folder
     * @param target other folder or file (root case)
     */
    private _moveFolders(source: IFolderGridFile, target?: IFolderGridFile) {
        if (source.type !== 'folder') return;
        const editedFolder: SaveFolderInput = {
            id: source.id,
            parentFolderId: target?.type === 'folder' ? target?.id : target?.parentId,
            name: source.name
        };
        this.onFolderDropped.emit({ source, target });
        this._store.dispatch(saveUserFolder({ folder: editedFolder }));
    }

    /*
     * Refreshes the cells and header to reflect the changes when the data is updated.
     * @param {RowDataUpdatedEvent} event - The event object containing information about the row data update.
     * @return {void} This method does not return any value.
     */
    protected onRowDataUpdated(event: RowDataUpdatedEvent): void {
        this.rowDataUpdated.emit(event);
        event.api.refreshCells({ force: true });
        event.api.refreshHeader();
    }
}
